import { useContext, useEffect } from 'react';
import ZillowMapContext from '@zillow/zillow-map-control/dist/ZillowMapContext';

interface NumberedDotProps {
  lat: number;
  lng: number;
  label: number | string;
  onClick?: () => void;
}

const createMarkerIcon = (label: number | string): string => {
  const labelLength = String(label).length;
  // Calculate dynamic dimensions based on label length
  const viewBoxWidth = Math.max(20, labelLength * 6 + 4); // 6 units per character + padding
  const viewBoxHeight = 20;
  const svgWidth = viewBoxWidth * 2; // Scale up for actual display size
  const svgHeight = 40;
  const rectWidth = viewBoxWidth - 6; // Leave 3 units margin on each side
  const rectX = 3;

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${viewBoxWidth} ${viewBoxHeight}">
      <rect x="${rectX}" y="4" width="${rectWidth}" height="12" rx="5" ry="4" fill="#891EFA" stroke="white" stroke-width="0.5" />
      <text x="50%" y="50%" text-anchor="middle" dominant-baseline="central"
            font-family="Arial" font-size="6" fill="white">${label}</text>
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

export const NumberedDot = ({ lat, lng, label, onClick }: NumberedDotProps) => {
  const { gmap } = useContext<{ gmap: google.maps.Map }>(ZillowMapContext);

  useEffect(() => {
    if (!gmap || !window.google?.maps?.Data) return;

    const point = new google.maps.LatLng(lat, lng);
    const labelLength = String(label).length;
    const viewBoxWidth = Math.max(20, labelLength * 6 + 4);
    const svgWidth = viewBoxWidth * 2;

    const marker = new google.maps.Marker({
      position: point,
      icon: {
        url: createMarkerIcon(label),
        anchor: new google.maps.Point(svgWidth / 2, 20),
      },
      cursor: 'pointer',
    });

    marker.setMap(gmap);

    const clickListener = onClick
      ? marker.addListener('click', (e: google.maps.MapMouseEvent) => {
          e.stop();
          onClick();
        })
      : null;

    // Cleanup function to remove the marker when component unmounts or dependencies change
    return () => {
      if (clickListener) {
        google.maps.event.removeListener(clickListener);
      }
      marker.setMap(null);
    };
  }, [gmap, lat, lng, label, onClick]);

  return null;
};
